import { Formik } from 'formik';
import { useRef } from 'react';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import * as Yup from 'yup';
import usePostHogCapture from '../../hooks/usePostHogCapture';
import { EmailSchema } from '../../utils/yup/EmailSchema';
import AuthUI from './components/AuthUI';
import EmailConfirmation from './components/EmailConfirmation';

const initialValues = {
  email: '',
  rememberMe: false,
};

const validationSchema = Yup.object().shape({
  email: EmailSchema(true),
  rememberMe: Yup.bool(),
});

export interface AuthFormFields {
  email: string;
  rememberMe: boolean;
}

const LOGIN_REQUEST_URL = '/auth/login/request';

export type AuthUIMode = 'signup' | 'login';

const getAuthMode = (
  pathname: string,
  routeState?: { mode?: AuthUIMode } | null,
): AuthUIMode => {
  if (pathname === '/signup') {
    return 'signup';
  }

  if (routeState?.mode === 'signup') {
    return 'signup';
  }

  return 'login';
};

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const postHogCapture = usePostHogCapture();
  const redirectUriRef = useRef(searchParams.get('redirect_uri'));
  const authMode = getAuthMode(
    location.pathname,
    location.state as { mode?: AuthUIMode } | null,
  );

  const handleSubmit = async (
    values: { email: string; rememberMe?: boolean },
    setStatus: (status?: any) => void,
  ) => {
    try {
      const response = await fetch(LOGIN_REQUEST_URL, {
        method: 'POST',
        body: JSON.stringify({
          email: values.email,
          rememberMe: values.rememberMe,
          loginMethod: 'MagicLink',
          redirectUri: redirectUriRef.current,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status !== 200) {
        let error;

        const body = await response.json();
        if (body.message) {
          error = body.message;
        }

        setStatus({
          submit: error ?? 'Something went wrong! Please try again later.',
        });
      } else {
        navigate('/auth/email-confirmation', { state: { mode: authMode } });
        setStatus({});
        postHogCapture('magic_link_requested', {
          email: values.email,
          mode: authMode,
        });
      }
    } catch (e) {
      setStatus({ submit: 'Something went wrong! Please try again later.' });
      throw e;
    }
  };

  const authUI = (
    <AuthUI
      mode={authMode}
      changeMode={() => navigate(authMode === 'signup' ? '/' : '/signup')}
    />
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, { setStatus }) => handleSubmit(values, setStatus)}
      validationSchema={validationSchema}
    >
      <Routes>
        <Route path='/' element={authUI} />
        <Route path='/signup' element={authUI} />
        <Route
          path='/email-confirmation'
          element={<EmailConfirmation mode={authMode} />}
        />
      </Routes>
    </Formik>
  );
}
